// The admin app (showman-admin-ui, same origin in production) keeps a PowerSync local
// database per event, the signed-in user's synced rows (buyer names, emails, phones), in
// the origin-private file system (OPFS) under one folder, or, where OPFS is refused (e.g.
// Safari private windows), in IndexedDB databases named `powersync/<event id>.sqlite`. It
// records whose rows they are, and which databases finished their first sync, under
// localStorage keys. Keep the names in step with src/sync/stores.ts and owner.ts there.
export const POWERSYNC_OPFS_DIRECTORY = 'powersync'
// Every IndexedDB fallback database's name starts with this.
export const POWERSYNC_INDEXEDDB_PREFIX = 'powersync/'
export const POWERSYNC_OWNER_KEY = 'showman.powersync.owner'
export const POWERSYNC_SYNCED_KEY = 'showman.powersync.synced'

function removeOpfsFolder (fileSystem) {
  if (!fileSystem || !fileSystem.getDirectory) return Promise.resolve()
  return fileSystem.getDirectory()
    .then(root => root.removeEntry(POWERSYNC_OPFS_DIRECTORY, {recursive: true}))
}

// Settles once the database is deleted, or the delete is blocked by an open admin tab
// (it still goes through once that tab closes).
function deleteIndexedDbNamed (indexedDb, name) {
  return new Promise(resolve => {
    const request = indexedDb.deleteDatabase(name)
    request.onsuccess = request.onerror = request.onblocked = () => resolve()
  })
}

// The admin app doesn't use the IndexedDB fallback where databases can't be listed, so
// there are none to delete there.
function deleteIndexedDbs (indexedDb) {
  if (!indexedDb || typeof indexedDb.databases !== 'function') return Promise.resolve()
  return indexedDb.databases().then(databases => Promise.all(databases
    .map(database => database.name)
    .filter(name => name && name.startsWith(POWERSYNC_INDEXEDDB_PREFIX))
    .map(name => deleteIndexedDbNamed(indexedDb, name))))
}

// Deletes the admin app's local databases on logout. People log out here, often with no
// admin tab open, so the admin app's own sign-out cleanup may never run.
//
// The "first sync done" records go first, so the admin app never trusts a database this
// deleted, even partly. The owner is left for the admin app to forget: an open admin
// tab's sign-out watcher only cleans up (closes its copy, reports unsent changes) while it
// can still see who owned the rows, and the next admin load forgets it otherwise. Never
// rejects.
export function clearAdminPowerSync ({storage, fileSystem, indexedDb} = {}) {
  try {
    storage = storage || window.localStorage
    fileSystem = fileSystem || (window.navigator && window.navigator.storage)
    indexedDb = indexedDb || window.indexedDB
    if (!storage.getItem(POWERSYNC_OWNER_KEY)) return Promise.resolve()
    storage.removeItem(POWERSYNC_SYNCED_KEY)
  } catch (e) {
    return Promise.resolve() // Storage blocked: nothing was synced in this browser either.
  }

  const settled = promise => Promise.resolve().then(() => promise()).catch(() => {})
  return Promise.all([
    settled(() => removeOpfsFolder(fileSystem)),
    settled(() => deleteIndexedDbs(indexedDb))
  ]).then(() => {})
}
